import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import { RefreshCw, Trash2 } from 'lucide-react';
import { formatCurrency, formatDate } from '../utils/formatters';

export const InspectRecordedAllocationsDialog = ({
  open,
  onOpenChange,
  inspectRecId,
  inspectRecData,
  inspectRecLoading,
  deleteAllocationDoc,
  fetchReconciliation,
  reconInvestorFilter,
  reconFromDate,
  reconToDate,
  forceCleanRecorded,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-3xl" data-testid="inspect-recorded-allocations-dialog">
      <DialogHeader>
        <DialogTitle>Allocations on recorded txn <span className="font-mono text-sm">{inspectRecId?.slice(0, 14)}…</span></DialogTitle>
        <DialogDescription>
          Every allocation record that references this recorded transaction — including orphaned / phantom ones that the
          regular Allocate dialog couldn't show. Click <Trash2 className="inline h-3 w-3" /> on a row to reverse just that
          allocation, or <strong>Force-Clean All</strong> to nuke every reference and put the recorded back into Unmatched.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3 max-h-[60vh] overflow-y-auto">
        {inspectRecLoading && <div className="p-6 text-center text-gray-500">Loading…</div>}
        {inspectRecData && (
          <>
            <div className="text-sm p-2 bg-slate-50 border rounded">
              <span className="font-semibold">Allocation records found:</span> {inspectRecData.total_records}
            </div>
            {inspectRecData.allocation_records.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-500">
                No allocation records reference this txn. If the UI still shows "Already allocated", click <strong>Refresh Recon</strong> below.
              </div>
            ) : (
              inspectRecData.allocation_records.map((rec, i) => {
                const bank = rec.bank || {};
                const isPhantom = bank.id && bank.exists === false;
                return (
                  <div key={i} className={`border rounded p-3 space-y-2 ${isPhantom ? 'bg-rose-50 border-rose-300' : 'bg-white'}`} data-testid={`inspect-rec-alloc-${i}`}>
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex flex-wrap gap-3 text-slate-600">
                        <span>Kind: <span className="font-mono">{rec.kind || 'bank'}</span></span>
                        <span>Status: <span className="font-mono">{rec.status || '-'}</span></span>
                        <span>This recorded got: <span className="font-mono font-bold text-indigo-700">{formatCurrency(rec.allocated_amount_for_this_recorded)}</span></span>
                        {rec.write_off_amount_for_this_recorded > 0 && <span className="text-amber-700">Write-off: {formatCurrency(rec.write_off_amount_for_this_recorded)}</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-400 text-[10px] font-mono">{rec.allocation_id?.slice(0, 8)}</span>
                        <button
                          onClick={() => deleteAllocationDoc(rec.allocation_id)}
                          className="text-red-500 hover:text-red-700"
                          title="Reverse this allocation"
                          data-testid={`inspect-rec-delete-${i}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div className={`text-xs p-2 rounded border ${isPhantom ? 'bg-rose-100 border-rose-300' : 'bg-slate-50 border-slate-200'}`}>
                      {bank.id ? (
                        <>
                          <div className="font-mono text-[11px] truncate">Bank: {bank.id}</div>
                          {bank.exists ? (
                            <div className="text-slate-700 mt-0.5 flex flex-wrap gap-2">
                              <span>{formatDate(bank.posting_date)}</span>
                              <span className="font-mono">{formatCurrency(bank.amount)}</span>
                              <span className="truncate flex-1">{bank.description || '-'}</span>
                              {bank.reconciliation_status && <Badge className="bg-slate-200 text-[10px]">{bank.reconciliation_status}</Badge>}
                            </div>
                          ) : (
                            <div className="text-rose-700 mt-0.5 font-semibold">
                              ⚠ Bank txn no longer exists — this is a phantom allocation. Use the trash icon to remove it.
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-amber-700">No bank linked (write-off only)</div>
                      )}
                    </div>
                    {rec.detail_count > 1 && (
                      <div className="text-[10px] text-slate-500 italic">
                        This allocation also covers {rec.detail_count - 1} other recorded txn(s). Deleting will reverse them all.
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </>
        )}
      </div>
      <DialogFooter className="gap-2 flex-wrap">
        <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        <Button
          variant="outline"
          onClick={() => fetchReconciliation(reconInvestorFilter === 'all' ? '' : reconInvestorFilter, reconFromDate, reconToDate)}
          data-testid="inspect-rec-refresh-recon"
        >
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh Recon
        </Button>
        <Button
          variant="destructive"
          onClick={forceCleanRecorded}
          disabled={inspectRecLoading}
          data-testid="inspect-rec-force-clean"
        >
          <Trash2 className="h-4 w-4 mr-1" /> Force-Clean All
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
